/**
 * Maps Medical Necessity UI values to Amplitude governed enums.
 * @module medicalNecessityAnalytics/mappers
 */
import { NCCNEvaluationRecord } from "../../../data-model/nccnEvaluation";
import {
  EvaluationItem,
  EvaluationResult,
  FeedbackType,
  HumanVerdict,
} from "../../../data-model/nccnGuidelines";
import {
  FeedbackKind,
  isEffectiveMet,
} from "../utils/nccnReviewFeedback";

/** AI / human binary criterion answer for analytics. */
export type AiCriterionAnswer = "yes" | "no";

/** AI / human guideline verdict for analytics. */
export type AiGuidelineVerdict = "met" | "not_met";

/** Reviewer Correct / Incorrect selection. */
export type CriterionOutcome = "correct" | "incorrect";

/** Incorrect feedback reason type on Feedback Saved. */
export type IncorrectReasonType = "logic" | "evidence" | "comment";

/** Active RHS surface for PDF interaction context. */
export type RhsStage = "criterion" | "guideline_verdict" | "summary";

/**
 * Maps an AI evaluation result to a binary criterion answer.
 *
 * @param verdict - AI criterion verdict from the evaluation item
 * @returns `yes` / `no`, or undefined when Undetermined / missing
 */
export const mapAiCriterionAnswer = (
  verdict: EvaluationResult | null | undefined,
): AiCriterionAnswer | undefined => {
  if (verdict === "True") return "yes";
  if (verdict === "False") return "no";
  return undefined;
};

/**
 * Maps an AI guideline final result to a binary verdict.
 *
 * @param result - AI guideline final_result
 * @returns `met` / `not_met`, or undefined when Undetermined / missing
 */
export const mapAiGuidelineVerdict = (
  result: EvaluationResult | null | undefined,
): AiGuidelineVerdict | undefined => {
  if (result === "True") return "met";
  if (result === "False") return "not_met";
  return undefined;
};

/**
 * Maps a stored human guideline verdict to the analytics enum.
 *
 * @param verdict - Human review verdict
 * @returns `met` / `not_met`, or undefined when absent
 */
export const mapHumanGuidelineVerdict = (
  verdict: HumanVerdict | null | undefined,
): AiGuidelineVerdict | undefined => {
  if (verdict === HumanVerdict.Met) return "met";
  if (verdict === HumanVerdict.NotMet) return "not_met";
  return undefined;
};

/**
 * Maps an order-level evaluation result to the analytics verdict enum.
 *
 * @param result - Order-level `EvaluationResult` (`True` / `False` / else)
 * @returns `met` / `not_met`, or undefined when Undetermined / missing
 */
export const mapOrderVerdict = (
  result: EvaluationResult | null | undefined,
): AiGuidelineVerdict | undefined => mapAiGuidelineVerdict(result);

/** How a guideline review was concluded. */
export type CompletionMode = "finalized" | "skipped";

/**
 * Counts guidelines by their effective met / not-met verdict (human verdict
 * when present, else the AI verdict).
 *
 * @param guidelines - All guideline records for the order
 * @returns Met and not-met counts
 */
export const countGuidelineVerdicts = (
  guidelines: NCCNEvaluationRecord[],
): { met: number; notMet: number } => {
  let met = 0;
  let notMet = 0;
  for (const guideline of guidelines) {
    if (isEffectiveMet(guideline)) met += 1;
    else notMet += 1;
  }
  return { met, notMet };
};

/**
 * Derives criterion_outcome from stored item feedback.
 *
 * @param item - Evaluation criterion item
 * @returns `correct` / `incorrect`, or undefined when unset
 */
export const mapCriterionOutcome = (
  item: EvaluationItem,
): CriterionOutcome | undefined => {
  if (item.ai_answer_feedback === FeedbackType.Like) return "correct";
  if (item.ai_answer_feedback === FeedbackType.Dislike) return "incorrect";
  return undefined;
};

/**
 * Derives human_criterion_answer for binary criteria only (Section 1.1).
 *
 * @param item - Evaluation criterion with AI answer and human Correct/Incorrect
 * @returns Human yes/no, or undefined when AI was Undetermined or outcome missing
 */
export const deriveHumanCriterionAnswer = (
  item: EvaluationItem,
): AiCriterionAnswer | undefined => {
  const ai = mapAiCriterionAnswer(item.verdict);
  const outcome = mapCriterionOutcome(item);
  if (!ai || !outcome) return undefined;
  if (outcome === "correct") return ai;
  return ai === "yes" ? "no" : "yes";
};

/**
 * Maps feedback kind to incorrect_reason_type.
 *
 * @param kind - Feedback form kind
 * @returns `logic`, `comment`, or `evidence` (chart references included)
 */
export const mapIncorrectReasonType = (
  kind: FeedbackKind,
): IncorrectReasonType => {
  if (kind === "incorrect_logic") return "logic";
  if (kind === "incorrect_comment") return "comment";
  return "evidence";
};
